/**
 * Velora Modern: the house look. theme.css is its foundation; this set
 * re-colours it for Zac's 2026-10-12 brief, scoped to `data-skin='velora'`
 * so the Familiar modes keep their own colours. Owner: WP0-DS.
 *
 * - No yellow or gold and no blue anywhere: neutral greys replace the navy
 *   cast, warning is red-orange (never ochre), info is neutral grey.
 * - Dark mode is a neutral charcoal (#121212 / #1a1a1a / #222 / #2a2a2a).
 * - The side navigation and the top bar are always dark charcoal (the
 *   `chrome` map), in light mode too; the content keeps light or dark.
 * - The accent stays the Velora orange. Primary buttons are charcoal on
 *   light and orange on dark.
 * - Clinical colours (`--clin-*`) are untouched: they are standard chart
 *   semantics.
 *
 * Every value is generated or a fixed hex here, inside @velora/tokens
 * (ADR-0007). WCAG AA is checked in skins.test.ts against CONTRAST_RULES.
 */
import { oklchToHex } from '../color.js';
import { generateRamp, LOGO, type Mode, type RampSpec } from '../ramps.js';
import {
  generateTheme,
  STATUS_TONES,
  tonalAccent,
  tonalAccentFg,
  type StatusTone,
} from '../theme.js';
import { defineSkinTokens, type TokenMap } from './index.js';

/** Neutral (chroma 0) grey at an OKLCH lightness. */
const grey = (l: number, alpha?: number): string => oklchToHex({ l, c: 0, h: 0 }, alpha);

const ORANGE = LOGO.orange; // #f7941d, the Velora orange
const ORANGE_DEEP = LOGO.orangeDeep; // #e8650a
const ORANGE_TEXT = LOGO.orangeText; // #b4470a, orange text on light
/** Lighter orange for dark surfaces (hue 52, not the gold #fbb040). */
const ORANGE_LIGHT = oklchToHex({ l: 0.8, c: 0.14, h: 52 });
const CHARCOAL = '#1a1a1a';

/** Dark neutrals, as Zac specified them. */
const DARK_NEUTRALS: TokenMap = {
  'surface-0': '#121212',
  'surface-1': '#1a1a1a',
  'surface-2': '#222222',
  'surface-3': '#2a2a2a',
  'surface-sunken': '#161616',
  'surface-hover': '#262626',
  'surface-active': '#303030',
  'border-subtle': '#2a2a2a',
  border: '#363636',
  'border-strong': '#4a4a4a',
  'border-control': '#6e6e6e',
  text: '#ededed',
  'text-muted': '#bdbdbd',
  'text-dim': '#a0a0a0',
  'text-disabled': '#6b6b6b',
};

/** Warning is red-orange, info is neutral grey; danger and success keep their hue. */
const TONES: Record<StatusTone, { hue: number; chroma: number }> = {
  danger: { hue: 20, chroma: 0.19 },
  warning: { hue: 42, chroma: 0.16 },
  success: { hue: 160, chroma: 0.14 },
  info: { hue: 0, chroma: 0 },
};

/**
 * Chart and office colours (dashboard viz audit, 2026-10-16). Orange first,
 * then no blue, cyan, yellow or grey: a grey slot reads as "disabled" or
 * "other", and the old one-lightness set failed the colour-blind check
 * (teal next to grey, delta E 1.2). Each slot has its own lightness per
 * mode so neighbours stay apart under protanopia and deuteranopia
 * (adjacent delta E >= 9.3, OKLab x100) and for full colour vision
 * (>= 17.3); the first three (offices 1-3, production vs collections) are
 * pairwise distinct; every mark is >= 3:1 on surface-1. Slots 3, 5 and 6
 * keep their old hue; slot 2 is brown / sand since MODULE-AUDIT 2026-10-20
 * (it was violet). Re-run the dataviz validator when a value changes
 * (docs/ux/dashboard-viz-audit-2026-10-16.md).
 */
const CHART: readonly { hue: number; chroma: number; l: Readonly<Record<Mode, number>> }[] = [
  { hue: 55, chroma: 0.16, l: { light: 0.62, dark: 0.66 } }, // orange (brand)
  // Was violet (hue 305), which reads blue-violet (MODULE-AUDIT X1, no
  // blue). A warm brown in light mode, sand in dark: same hue family, far
  // from orange and teal by lightness.
  { hue: 50, chroma: 0.11, l: { light: 0.43, dark: 0.82 } }, // brown / sand
  { hue: 180, chroma: 0.12, l: { light: 0.63, dark: 0.64 } }, // teal
  { hue: 350, chroma: 0.15, l: { light: 0.45, dark: 0.54 } }, // magenta
  { hue: 140, chroma: 0.15, l: { light: 0.54, dark: 0.56 } }, // green
  { hue: 15, chroma: 0.17, l: { light: 0.69, dark: 0.66 } }, // coral
  { hue: 330, chroma: 0.14, l: { light: 0.45, dark: 0.54 } }, // plum
  { hue: 60, chroma: 0.1, l: { light: 0.66, dark: 0.6 } }, // tan
];

/**
 * Raw ramps some screens use directly (schedule appointment types,
 * Marketplace tones, the sign-in backdrop): navy and charcoal become
 * neutral, blue becomes graphite, amber becomes red-orange, violet rose.
 */
const RAMPS: readonly RampSpec[] = [
  { name: 'navy', hue: 0, chroma: 0 },
  { name: 'charcoal', hue: 0, chroma: 0 },
  { name: 'blue', hue: 0, chroma: 0 },
  { name: 'amber', hue: 42, chroma: 0.15 },
  // Violet reads blue-violet (MODULE-AUDIT X1): office chips, the Referred
  // legend, "Insurance check" and the schedule's violet tone become rose.
  { name: 'violet', hue: 350, chroma: 0.11 },
];

function ramps(mode: Mode): Record<string, string> {
  const out: Record<string, string> = {};
  for (const spec of RAMPS) {
    generateRamp(spec, mode).forEach((hex, i) => {
      out[`${spec.name}-${i + 1}`] = hex;
    });
  }
  return out;
}

function status(mode: Mode): Record<string, string> {
  const t: Record<string, string> = {};
  for (const tone of STATUS_TONES) {
    const { hue, chroma } = TONES[tone];
    if (mode === 'dark') {
      t[tone] = tone === 'info' ? '#c4c4c4' : oklchToHex({ l: 0.76, c: chroma, h: hue });
      t[`${tone}-soft`] = oklchToHex({ l: 0.27, c: chroma * 0.3, h: hue });
      t[`${tone}-border`] = oklchToHex({ l: 0.43, c: chroma * 0.55, h: hue });
      t[`${tone}-solid`] = oklchToHex({ l: 0.5, c: chroma, h: hue });
    } else {
      t[tone] = tone === 'info' ? '#525252' : oklchToHex({ l: 0.5, c: chroma, h: hue });
      t[`${tone}-soft`] = oklchToHex({ l: 0.965, c: chroma * 0.17, h: hue });
      t[`${tone}-border`] = oklchToHex({ l: 0.84, c: chroma * 0.5, h: hue });
      t[`${tone}-solid`] = oklchToHex({ l: 0.5, c: chroma, h: hue });
    }
    t[`${tone}-solid-fg`] = '#ffffff';
  }
  return t;
}

function charts(mode: Mode): Record<string, string> {
  const t: Record<string, string> = Object.fromEntries(
    CHART.map(({ hue, chroma, l }, i) => [
      `chart-${i + 1}`,
      oklchToHex({ l: l[mode], c: chroma, h: hue }),
    ]),
  );
  // De-emphasis grey: the context marks when one mark is the point (the
  // current month) and the remainder of a ring meter. Never a series.
  t['chart-muted'] = mode === 'dark' ? grey(0.45) : grey(0.8);
  return t;
}

const GRADIENT = (deg: number) =>
  `linear-gradient(${deg}deg, ${ORANGE_LIGHT} 0%, ${ORANGE} 45%, ${ORANGE_DEEP} 100%)`;

function brand(mode: Mode): Record<string, string> {
  const dark = mode === 'dark';
  return {
    primary: dark ? ORANGE : CHARCOAL,
    'primary-hover': dark ? ORANGE_LIGHT : '#2e2e2e',
    'primary-active': dark ? ORANGE_DEEP : '#000000',
    'primary-fg': dark ? '#121212' : '#ffffff',
    accent: ORANGE,
    'accent-strong': ORANGE_DEEP,
    'accent-text': dark ? ORANGE_LIGHT : ORANGE_TEXT,
    'accent-soft': dark
      ? oklchToHex({ l: 0.27, c: 0.045, h: 52 })
      : oklchToHex({ l: 0.965, c: 0.03, h: 52 }),
    'focus-ring': dark ? ORANGE_LIGHT : ORANGE_TEXT,
    // The schedule's "Here" light and other yellow status dots (MODULE-AUDIT
    // X2): brand orange with the dark glyph, never yellow.
    'signal-yellow': ORANGE,
    'signal-yellow-fg': '#121212',
    selection: dark ? grey(0.36) : oklchToHex({ l: 0.9, c: 0.04, h: 55 }),
    'row-selected': dark ? grey(0.27) : oklchToHex({ l: 0.958, c: 0.018, h: 55 }),
    'navy-cast': dark ? grey(0.24) : grey(0.963),
    'brand-gradient': GRADIENT(135),
    'brand-gradient-vertical': GRADIENT(180),
    'brand-gradient-fg': '#121212',
    'brand-ink': dark ? '#ffffff' : CHARCOAL,
    'brand-mark-ink': dark ? '#ededed' : CHARCOAL,
    'brand-mark-ink-2': dark ? '#a0a0a0' : '#4a4a4a',
    'brand-orange-light': ORANGE_LIGHT,
    overlay: grey(0.1, dark ? 0.7 : 0.4),
  };
}

/**
 * The tonal accent (owner, 2026-10-16): primary actions, the hero button,
 * accent icon tiles and checked controls are a translucent orange with a
 * 1px border and a readable orange label, never a solid orange fill. The
 * engine roles (packages/ui/src/skins/engine.css) default to the solid
 * primary, so the Familiar modes keep their product look; Velora Modern
 * points them at the `accent-soft-*` tokens here. Concrete values, not
 * var() references, so nested light/dark sections and the always-dark
 * chrome resolve their own.
 */
function tonal(mode: Mode, surface: string): Record<string, string> {
  const t = tonalAccent(mode, tonalAccentFg(mode), surface);
  const bg = t['accent-soft-bg'] as string;
  const hover = t['accent-soft-bg-hover'] as string;
  const active = t['accent-soft-bg-active'] as string;
  const border = t['accent-soft-border'] as string;
  const fg = t['accent-soft-fg'] as string;
  return {
    ...t,
    'action-bg': bg,
    'action-bg-hover': hover,
    'action-bg-active': active,
    'action-border': border,
    'action-fg': fg,
    'action-hero-bg': hover,
    'action-hero-bg-hover': active,
    'action-hero-border': border,
    'action-hero-fg': fg,
    'accent-tile-bg': bg,
    'accent-tile-border': border,
    'accent-tile-fg': fg,
    'accent-control-bg': active,
    'accent-control-border': fg,
    'accent-control-fg': fg,
    'accent-count-bg': t['accent-soft-opaque'] as string,
    'accent-count-fg': fg,
    'nav-active-bg': bg,
    'nav-active-bar': fg,
  };
}

/** The neutral (no navy cast) neutrals, glass, shadows and skeletons for one mode. */
function neutrals(mode: Mode): Record<string, string> {
  const base = generateTheme({ mode, contrast: 0, base: { hue: 0, chromaScale: 0 } });
  const keys = [
    'chrome-glass',
    'skeleton',
    'skeleton-shine',
    'shadow-1',
    'shadow-2',
    'shadow-popover',
    'shadow-hover',
  ];
  const out: Record<string, string> = Object.fromEntries(keys.map((k) => [k, base[k] as string]));
  if (mode === 'dark') return { ...out, ...DARK_NEUTRALS };
  for (const k of Object.keys(DARK_NEUTRALS)) out[k] = base[k] as string;
  return out;
}

function themeFor(mode: Mode): Record<string, string> {
  const n = neutrals(mode);
  const t = {
    ...n,
    ...brand(mode),
    ...tonal(mode, n['surface-1'] as string),
    ...status(mode),
    ...charts(mode),
    ...ramps(mode),
  };
  t['chart-grid'] = t['border-subtle'] as string;
  t['chart-axis'] = t['text-dim'] as string;
  return t;
}

const light = themeFor('light');
const dark = themeFor('dark');

/** Side navigation and top bar: the dark set, solid (no see-through glass). */
const chrome: TokenMap = {
  ...dark,
  'surface-0': '#141414',
  'chrome-glass': '#141414',
};

export const tokens = defineSkinTokens({ light, dark, chrome });

/** Exposed for the contrast test. */
export const VELORA_MODERN = { light, dark, chrome } as const;
